import { useState, useEffect } from "react";
import {
  Search, X, RefreshCw, UserPlus, KeyRound, ShieldCheck,
  Power, PowerOff, Mail, Phone, Copy, Check, RotateCcw,
} from "lucide-react";

import { supportTeam as supportTeamApi, roles as rolesApi, permissions as permissionsApi } from "../lib/api.js";
import { useApi, useDebounce } from "../hooks/index.js";
import { Spinner, Empty, CloseX, Pagination } from "../components/ui.jsx";
import { useLang } from "../lib/i18n.jsx";

function fmtTime(date) {
  if (!date) return "—";
  return new Date(date).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// ─── COPY FIELD ───────────────────────────────────────────────────────────────

function CopyField({ value }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <input className="f-in" value={value} readOnly style={{ fontFamily: "var(--fm)" }} />
      <button className="btn btn-out btn-sm" onClick={copy} type="button">
        {copied ? <><Check size={12} /> {t("supportTeam.copied")}</> : <><Copy size={12} /> {t("supportTeam.copy")}</>}
      </button>
    </div>
  );
}

// ─── CREATE MODAL ─────────────────────────────────────────────────────────────

function CreateModal({ allRoles, onClose, onCreated, toast }) {
  const { t } = useLang();
  const [name, setName]     = useState("");
  const [email, setEmail]   = useState("");
  const [phone, setPhone]   = useState("");
  const [roleIds, setRoleIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [credentials, setCredentials] = useState(null);

  const toggleRole = (id) => {
    setRoleIds(r => r.includes(id) ? r.filter(x => x !== id) : [...r, id]);
  };

  const submit = async () => {
    if (!name || !email) { toast(t("supportTeam.nameEmailRequired")); return; }
    setSaving(true);
    try {
      const res = await supportTeamApi.create({ name, email, phone: phone || undefined, roleIds });
      toast(t("supportTeam.memberCreated"));
      onCreated();
      if (res?.tempPassword) {
        setCredentials({ email, tempPassword: res.tempPassword });
      } else {
        onClose();
      }
    } catch (e) {
      toast(e.message ?? t("supportTeam.createFailed"));
    } finally {
      setSaving(false);
    }
  };

  if (credentials) {
    return (
      <div data-testid="credentials-modal" className="modal-ov open">
        <div className="modal">
          <div className="modal-ttl">{t("supportTeam.credentialsTitle")} <CloseX onClick={onClose} /></div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginBottom: 14 }}>{t("supportTeam.credentialsNote")}</div>
          <div className="f-grp">
            <label className="f-lbl">{t("supportTeam.email")}</label>
            <input data-testid="credentials-email" className="f-in" value={credentials.email} readOnly />
          </div>
          <div className="f-grp">
            <label className="f-lbl">{t("supportTeam.tempPassword")}</label>
            <CopyField data-testid="credentials-password" value={credentials.tempPassword} />
          </div>
          <button className="btn btn-teal" style={{ width: "100%", marginTop: 10 }} onClick={onClose}>
            {t("supportTeam.done")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-ov open">
      <div className="modal">
        <div className="modal-ttl">{t("supportTeam.newMember")} <CloseX onClick={onClose} /></div>
        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.name")}</label>
          <input className="f-in" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.email")}</label>
          <input className="f-in" type="email" value={email} onChange={e => setEmail(e.target.value)} />
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.phone")}</label>
          <input className="f-in" value={phone} onChange={e => setPhone(e.target.value)} />
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.roles")}</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allRoles.map(r => (
              <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
                {r.name}
              </label>
            ))}
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button data-testid="create-member-submit" className="btn btn-teal" style={{ flex: 1 }} onClick={submit} disabled={saving || !name || !email}>
            <UserPlus size={12} /> {saving ? t("common.sending") : t("supportTeam.create")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── EDIT MODAL ───────────────────────────────────────────────────────────────

function EditModal({ member, allRoles, groupedPermissions, onClose, onSaved, toast }) {
  const { t } = useLang();
  const [name, setName]   = useState(member.name ?? "");
  const [phone, setPhone] = useState(member.phone ?? "");
  const [roleIds, setRoleIds] = useState(member.roles?.map(r => allRoles.find(ar => ar.name === r.name)?.id).filter(Boolean) ?? []);
  const [permCodes, setPermCodes] = useState(member.permissions ?? []);
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState(null);

  const toggleRole = (id) => setRoleIds(r => r.includes(id) ? r.filter(x => x !== id) : [...r, id]);
  const togglePerm = (code) => setPermCodes(p => p.includes(code) ? p.filter(x => x !== code) : [...p, code]);

  const save = async () => {
    setSaving(true);
    try {
      await supportTeamApi.update(member.id, { name, phone: phone || null });
      await supportTeamApi.updateRoles(member.id, roleIds);
      await supportTeamApi.updatePermissions(member.id, permCodes);
      toast(t("supportTeam.memberUpdated"));
      onSaved();
      onClose();
    } catch (e) {
      toast(e.message ?? t("supportTeam.updateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    try {
      if (member.isActive) await supportTeamApi.deactivate(member.id);
      else await supportTeamApi.activate(member.id);
      toast(member.isActive ? t("supportTeam.deactivated") : t("supportTeam.activated"));
      onSaved();
      onClose();
    } catch (e) {
      toast(e.message ?? t("supportTeam.updateFailed"));
    }
  };

  const resetPassword = async () => {
    try {
      await supportTeamApi.resetPassword(member.id);
      toast(t("supportTeam.resetEmailSent"));
    } catch (e) {
      toast(e.message ?? t("supportTeam.updateFailed"));
    }
  };

  const regeneratePassword = async () => {
    if (!window.confirm(t("supportTeam.confirmRegenerate"))) return;
    try {
      const res = await supportTeamApi.regeneratePassword(member.id);
      setNewPassword(res.tempPassword);
      toast(t("supportTeam.passwordRegenerated"));
    } catch (e) {
      toast(e.message ?? t("supportTeam.regenerateFailed"));
    }
  };

  return (
    <div className="modal-ov open">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-ttl">{t("supportTeam.editMember")} <CloseX onClick={onClose} /></div>

        <div style={{ background: "var(--surface)", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 12, color: "var(--muted)" }}>
          <Mail size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />{member.email}
        </div>

        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.name")}</label>
          <input className="f-in" value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.phone")}</label>
          <input className="f-in" value={phone} onChange={e => setPhone(e.target.value)} />
        </div>

        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.roles")}</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {allRoles.map(r => (
              <label key={r.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                <input type="checkbox" checked={roleIds.includes(r.id)} onChange={() => toggleRole(r.id)} />
                {r.name}
              </label>
            ))}
          </div>
        </div>

        <div className="f-grp">
          <label className="f-lbl">{t("supportTeam.directPermissions")}</label>
          <div style={{ maxHeight: 220, overflowY: "auto", border: "1px solid var(--border)", borderRadius: 8, padding: 10 }}>
            {Object.entries(groupedPermissions).map(([category, perms]) => (
              <div key={category} style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: "var(--ghost)", textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 4 }}>{category}</div>
                {perms.map(p => (
                  <label key={p.code} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, cursor: "pointer", padding: "2px 0" }}>
                    <input type="checkbox" checked={permCodes.includes(p.code)} onChange={() => togglePerm(p.code)} />
                    {p.code}
                  </label>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
          <button className="btn btn-out btn-sm" onClick={resetPassword}>
            <KeyRound size={12} /> {t("supportTeam.sendResetEmail")}
          </button>
          <button className="btn btn-out btn-sm" onClick={regeneratePassword}>
            <RotateCcw size={12} /> {t("supportTeam.regeneratePassword")}
          </button>
          <button className={`btn btn-sm ${member.isActive ? "btn-out" : "btn-teal"}`} onClick={toggleActive}>
            {member.isActive ? <><PowerOff size={12} /> {t("supportTeam.deactivate")}</> : <><Power size={12} /> {t("supportTeam.activate")}</>}
          </button>
        </div>

        {newPassword && (
          <div className="f-grp">
            <label className="f-lbl">{t("supportTeam.tempPassword")}</label>
            <CopyField value={newPassword} />
          </div>
        )}

        <div style={{ display: "flex", gap: 10 }}>
          <button className="btn btn-out" onClick={onClose}>{t("common.cancel")}</button>
          <button className="btn btn-teal" style={{ flex: 1 }} onClick={save} disabled={saving}>
            {saving ? t("common.sending") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────

export default function PageSupportTeam({ toast, search: externalSearch, hasPermission }) {
  const { t } = useLang();
  const [search, setSearch] = useState(externalSearch ?? "");
  useEffect(() => { if (externalSearch !== undefined) setSearch(externalSearch); }, [externalSearch]);
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const limit = 20;

  const debSearch = useDebounce(search, 400);

  const { data, loading, reload } = useApi(
    () => supportTeamApi.list({ page, limit, ...(debSearch && { search: debSearch }) }),
    [page, debSearch]
  );
  const { data: rolesData, reload: reloadRoles } = useApi(() => rolesApi.list(), []);
  const { data: permsData } = useApi(() => permissionsApi.list(), []);

  const members = data?.members ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const allRoles = rolesData?.roles ?? [];
  const groupedPermissions = permsData?.grouped ?? {};

  const reset = () => { setSearch(""); setPage(1); };

  return (
    <div>
      <div className="sec-hd">
        <div>
          <div className="sec-ttl">{t("supportTeam.title")}</div>
          <div className="sec-sub">{t("supportTeam.found", { n: total.toLocaleString() })}</div>
        </div>
        {hasPermission?.('MANAGE_SUPPORT_TEAM') && (
          <button data-testid="create-member-button" className="btn btn-teal" onClick={() => setCreateOpen(true)}>
            <UserPlus size={12} /> {t("supportTeam.newMember")}
          </button>
        )}
      </div>

      <div className="filter-bar">
        <div className="tb-search" style={{ maxWidth: 320, flex: 1 }}>
          <Search size={14} color="var(--ghost)" />
          <input
            placeholder={t("supportTeam.searchPlaceholder")}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
          {search && (
            <button onClick={() => { setSearch(""); setPage(1); }}
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--ghost)", display: "flex", alignItems: "center" }}>
              <X size={12} />
            </button>
          )}
        </div>
        {search && (
          <button className="btn btn-out btn-sm" onClick={reset}>
            <RefreshCw size={12} /> {t("users.clear")}
          </button>
        )}
      </div>

      <div className="card">
        {loading ? <Spinner /> : members.length === 0
          ? <div style={{ padding: 24 }}><Empty icon="🛡" message={t("supportTeam.noneFound")} /></div>
          : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>{t("tbl.user")}</th><th>{t("supportTeam.phone")}</th><th>{t("supportTeam.roles")}</th>
                    <th>{t("tbl.status")}</th><th>{t("users.lastLogin")}</th><th>{t("tbl.action")}</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr data-testid="support-team-row" key={m.id} style={hasPermission?.('MANAGE_SUPPORT_TEAM') ? { cursor: "pointer" } : {}} onClick={() => hasPermission?.('MANAGE_SUPPORT_TEAM') && setEditMember(m)}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: "linear-gradient(135deg, var(--teal), #086b47)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, color: "white", fontWeight: 700, flexShrink: 0 }}>
                            {m.name?.[0]?.toUpperCase() ?? "?"}
                          </div>
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{m.name}</div>
                            <div style={{ fontSize: 10, color: "var(--ghost)" }}>{m.email}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {m.phone ? <><Phone size={10} style={{ verticalAlign: "middle", marginRight: 4 }} />{m.phone}</> : "—"}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {m.roles?.length
                            ? m.roles.map(r => <span key={r.id} className="badge bg-blue" style={{ fontSize: 9 }}>{r.name}</span>)
                            : <span style={{ fontSize: 11, color: "var(--ghost)" }}>—</span>}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${m.isActive ? "bg-teal" : "bg-gray"}`}>
                          {m.isActive ? t("supportTeam.active") : t("supportTeam.inactive")}
                        </span>
                      </td>
                      <td style={{ fontFamily: "var(--fm)", fontSize: 11, color: "var(--ghost)" }}>{fmtTime(m.lastLoginAt)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        {hasPermission?.('MANAGE_SUPPORT_TEAM') && (
                          <button className="btn btn-out btn-xs" onClick={() => setEditMember(m)}>
                            <ShieldCheck size={10} /> {t("supportTeam.manage")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        <Pagination page={page} totalPages={totalPages} total={total} onPrev={() => setPage(p => p - 1)} onNext={() => setPage(p => p + 1)} />
      </div>

      {createOpen && (
        <CreateModal allRoles={allRoles} onClose={() => setCreateOpen(false)} onCreated={reload} toast={toast} />
      )}
      {editMember && (
        <EditModal
          member={editMember}
          allRoles={allRoles}
          groupedPermissions={groupedPermissions}
          onClose={() => setEditMember(null)}
          onSaved={() => { reload(); reloadRoles(); }}
          toast={toast}
        />
      )}
    </div>
  );
}
